// Aqua-fleet vessels on the public map: Isotype-style pictogram markers, a
// laden ("carrying fish") badge, on-select 7-day tracks, and a detail panel.
//
// The marker encodes *what the vessel is doing* (state → colour + pictogram),
// its *role* (size band → marker size), and whether it is *likely carrying
// fish* (laden → a small fish badge). All derived server-side by
// /v1/vessels/positions; see api/main.py + FINDINGS_vessel_operations.md.

import maplibregl from "maplibre-gl";

import { insightCompanyUrl, insightVesselUrl, insightLocationUrl } from "./insight";

const API_BASE = import.meta.env.VITE_API_BASE;

export type VesselRole = "wellboat" | "carrier" | "service";
export type VesselState = "transit" | "reception" | "farm" | "idle";

export const VESSEL_ROLES: { key: VesselRole; label: string; sub: string }[] = [
  { key: "wellboat", label: "Brønnbåt / storfartøy", sub: "≥ 40 m" },
  { key: "carrier", label: "Mellomfartøy", sub: "25–40 m" },
  { key: "service", label: "Servicebåt", sub: "< 25 m" },
];

export const STATE_META: Record<VesselState, { label: string; color: string }> = {
  transit: { label: "Underveis", color: "#43c6a8" },
  reception: { label: "Ved slakteri / mottak", color: "#E63926" },
  farm: { label: "Ved anlegg", color: "#5bb98a" },
  idle: { label: "I ro · havn / oppankret", color: "#8b95a1" },
};

// Dominant historical operational role (op_role) — an ADDED dimension on top of
// the live `state` colour. Shown as a small coloured ring/badge on the marker
// and a legend so a user can tell smolt carriers / harvest wellboats /
// delousers / service-feed vessels apart at a glance.
export type VesselOpRole =
  | "smolt carrier"
  | "harvest wellboat"
  | "delousing / treatment"
  | "service / feed";

export const OPERATION_META: Record<VesselOpRole, { label: string; color: string }> = {
  "smolt carrier": { label: "Smoltbåt", color: "#7BE3C6" },
  "harvest wellboat": { label: "Brønnbåt – slakt/høst", color: "#E63926" },
  "delousing / treatment": { label: "Avlusing", color: "#F4A261" },
  "service / feed": { label: "Service/fôr", color: "#8AA99A" },
};

const OP_ROLE_KEYS = Object.keys(OPERATION_META) as VesselOpRole[];

// Live action the vessel is performing RIGHT NOW (current_op) — an ADDED
// dimension on top of `state` (colour) and `op_role` (historical type badge).
// Shown as a coloured halo/ring around the marker + a legend + a detail row.
// null = transiting / idle / service → no halo.
export type CurrentOp = "slaughter" | "delousing" | "harvest" | "smolt";

export const CURRENT_OP_META: Record<CurrentOp, { label: string; color: string }> = {
  slaughter: { label: "Til slakt", color: "#E63926" },
  delousing: { label: "Avlusing", color: "#F4A261" },
  harvest: { label: "Høsting", color: "#C77DFF" },
  smolt: { label: "Smolt", color: "#7BE3C6" },
};

const CURRENT_OP_KEYS = Object.keys(CURRENT_OP_META) as CurrentOp[];

type VesselProps = {
  mmsi: number;
  name: string | null;
  role: VesselRole;
  state: VesselState;
  op_role: VesselOpRole | null;
  current_op: CurrentOp | null;
  laden: boolean;
  length_m: number | null;
  ship_type: number | null;
  sog: number;
  cog: number | null;
  ts: string | null;
  age_h: number | null;
  last_op: string | null;
  last_op_at: string | null;
  operators: string[];
  from_site: string | null;
  from_operator: string | null;
  owner: string | null;
  owner_org: string | null;
  operator_company: string | null;
  vessel_type: string | null;
};

type VesselFC = GeoJSON.FeatureCollection<GeoJSON.Point, VesselProps>;

export type VesselFilter = {
  roles: Set<VesselRole>;
  ladenOnly: boolean;
  operatorSlug: string;
};
export const defaultVesselFilter = (): VesselFilter => ({
  roles: new Set<VesselRole>(["wellboat", "carrier", "service"]),
  ladenOnly: false,
  operatorSlug: "",
});

let positions: VesselFC = { type: "FeatureCollection", features: [] };
let onCountChange: ((shown: number, total: number) => void) | null = null;

export function setVesselCountListener(fn: (shown: number, total: number) => void): void {
  onCountChange = fn;
}

// ---------------------------------------------------------------------------
// Isotype pictograms · drawn to canvas, registered as map images @2x.
// ---------------------------------------------------------------------------

// Drawing happens in a "@2x" coordinate space (tokens 0..88, badge 0..44) but
// is rasterised SS× larger and registered at a matching pixelRatio, so the
// pictograms stay crisp even when MapLibre scales them up at high zoom.
const PX = 88;
const SS = 3;
const ICON_PR = 2 * SS;

function blankCtx(coord: number): { ctx: CanvasRenderingContext2D; dev: number } {
  const dev = coord * SS;
  const c = document.createElement("canvas");
  c.width = dev;
  c.height = dev;
  const ctx = c.getContext("2d")!;
  ctx.scale(SS, SS); // draw in 0..coord units, rasterise at SS× resolution
  return { ctx, dev };
}

// A boat silhouette (seen from above) pointing NORTH, coloured by state. The
// marker layer rotates it to the vessel's heading (cog), so the fleet reads as
// little ships sailing their actual course. Bow at top, transom at the bottom.
function token(state: VesselState): ImageData {
  const { ctx, dev } = blankCtx(PX);
  const hull = (): void => {
    ctx.beginPath();
    ctx.moveTo(44, 16); // bow tip (north)
    ctx.bezierCurveTo(54, 26, 56, 40, 56, 52); // starboard side
    ctx.lineTo(56, 64); // starboard transom corner
    ctx.lineTo(32, 64); // port transom corner
    ctx.lineTo(32, 52);
    ctx.bezierCurveTo(32, 40, 34, 26, 44, 16); // port side back to bow
    ctx.closePath();
  };
  ctx.save();
  ctx.shadowColor = "rgba(0,0,0,0.45)";
  ctx.shadowBlur = 6;
  ctx.shadowOffsetY = 2;
  hull();
  ctx.fillStyle = STATE_META[state].color;
  ctx.fill();
  ctx.restore();
  hull();
  ctx.lineWidth = 3.4;
  ctx.strokeStyle = "rgba(255,255,255,0.95)";
  ctx.lineJoin = "round";
  ctx.stroke();
  // Centreline / deck hint reinforcing the heading direction.
  ctx.beginPath();
  ctx.moveTo(44, 24);
  ctx.lineTo(44, 56);
  ctx.lineWidth = 2.4;
  ctx.strokeStyle = "rgba(255,255,255,0.5)";
  ctx.lineCap = "round";
  ctx.stroke();
  return ctx.getImageData(0, 0, dev, dev);
}

function fishBadge(): ImageData {
  const S = 44;
  const { ctx, dev } = blankCtx(S);
  // Small pulse disc with a white fish — the "carrying fish" mark.
  ctx.save();
  ctx.shadowColor = "rgba(0,0,0,0.4)";
  ctx.shadowBlur = 4;
  ctx.beginPath();
  ctx.arc(22, 22, 17, 0, Math.PI * 2);
  ctx.fillStyle = "#E63926";
  ctx.fill();
  ctx.restore();
  ctx.beginPath();
  ctx.arc(22, 22, 17, 0, Math.PI * 2);
  ctx.lineWidth = 3;
  ctx.strokeStyle = "#fff";
  ctx.stroke();
  // Fish body + tail.
  ctx.fillStyle = "#fff";
  ctx.beginPath();
  ctx.ellipse(21, 23, 9, 6, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(29, 23);
  ctx.lineTo(35, 18);
  ctx.lineTo(35, 28);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = "#E63926";
  ctx.beginPath();
  ctx.arc(17, 22, 1.6, 0, Math.PI * 2);
  ctx.fill();
  return ctx.getImageData(0, 0, dev, dev);
}

function opBadge(color: string): ImageData {
  const S = 44;
  const { ctx, dev } = blankCtx(S);
  // Small coloured disc with a white rim — the op_role tag, mirroring the
  // laden fish badge but keyed off the vessel's dominant historical role.
  ctx.save();
  ctx.shadowColor = "rgba(0,0,0,0.4)";
  ctx.shadowBlur = 4;
  ctx.beginPath();
  ctx.arc(22, 22, 15, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
  ctx.restore();
  ctx.beginPath();
  ctx.arc(22, 22, 15, 0, Math.PI * 2);
  ctx.lineWidth = 4;
  ctx.strokeStyle = "#fff";
  ctx.stroke();
  return ctx.getImageData(0, 0, dev, dev);
}

function opHalo(color: string): ImageData {
  // A coloured ring sized to sit just outside the state token (r=30 in PX
  // space), marking the vessel's LIVE action (current_op). Hollow centre so the
  // state pictogram + colour stay legible inside.
  const { ctx, dev } = blankCtx(PX);
  const cx = 44;
  const cy = 44;
  ctx.save();
  ctx.shadowColor = "rgba(0,0,0,0.35)";
  ctx.shadowBlur = 4;
  ctx.beginPath();
  ctx.arc(cx, cy, 37, 0, Math.PI * 2);
  ctx.lineWidth = 6;
  ctx.strokeStyle = color;
  ctx.stroke();
  ctx.restore();
  return ctx.getImageData(0, 0, dev, dev);
}

function registerIcons(map: maplibregl.Map): void {
  for (const s of ["transit", "reception", "farm", "idle"] as VesselState[]) {
    const id = `vstate-${s}`;
    if (!map.hasImage(id)) map.addImage(id, token(s), { pixelRatio: ICON_PR });
  }
  if (!map.hasImage("vbadge-fish"))
    map.addImage("vbadge-fish", fishBadge(), { pixelRatio: ICON_PR });
  for (const k of OP_ROLE_KEYS) {
    const id = `voprole-${k}`;
    if (!map.hasImage(id)) map.addImage(id, opBadge(OPERATION_META[k].color), { pixelRatio: ICON_PR });
  }
  for (const k of CURRENT_OP_KEYS) {
    const id = `vcurop-${k}`;
    if (!map.hasImage(id)) map.addImage(id, opHalo(CURRENT_OP_META[k].color), { pixelRatio: ICON_PR });
  }
}

// ---------------------------------------------------------------------------
// Layers
// ---------------------------------------------------------------------------

const ROLE_SIZE: maplibregl.ExpressionSpecification = [
  "match",
  ["get", "role"],
  "wellboat",
  1.0,
  "carrier",
  0.82,
  0.64,
];

// Only the four known op_roles get a badge (null / unknown → no badge).
const oproleFilter: maplibregl.FilterSpecification = [
  "in",
  ["get", "op_role"],
  ["literal", OP_ROLE_KEYS as string[]],
];

// Only the four known live ops get a halo (null → no halo).
const curopFilter: maplibregl.FilterSpecification = [
  "in",
  ["get", "current_op"],
  ["literal", CURRENT_OP_KEYS as string[]],
];

function addLayers(map: maplibregl.Map): void {
  // No clustering: at overview zoom the fleet reads as a density HEATMAP (sexy +
  // honest about where activity concentrates), and the individual pictograms
  // fade in as you zoom past the heatmap. Avoids all cluster-centroid artefacts.
  map.addSource("vessels", { type: "geojson", data: positions });
  // Locations a vessel is touching right now (at a farm / at a slaughterhouse) —
  // the origins for the animated sonar "pings".
  map.addSource("vessel-pings", { type: "geojson", data: computePings() });
  map.addSource("vessel-track", {
    type: "geojson",
    data: { type: "FeatureCollection", features: [] },
  });

  // Fleet-density heatmap — the overview surface. Fades out as the markers
  // (minzoom 7) fade in, so the two never fight.
  map.addLayer({
    id: "vessel-heat",
    type: "heatmap",
    source: "vessels",
    maxzoom: 9,
    layout: { visibility: "none" },
    paint: {
      // Laden vessels (fish aboard) weigh a little heavier — the heat tracks the
      // transport effort, not just hull count.
      "heatmap-weight": ["case", ["==", ["get", "laden"], true], 1.5, 1],
      "heatmap-intensity": ["interpolate", ["linear"], ["zoom"], 3, 0.7, 8, 1.5],
      "heatmap-radius": ["interpolate", ["linear"], ["zoom"], 3, 9, 6, 16, 9, 28],
      // Deep → mint → bright signal: brand-aligned glow.
      "heatmap-color": [
        "interpolate", ["linear"], ["heatmap-density"],
        0, "rgba(8,46,34,0)",
        0.2, "rgba(13,107,84,0.6)",
        0.45, "#1f9e7a",
        0.7, "#43c6a8",
        0.9, "#9bf0d4",
        1, "#eafff6",
      ],
      "heatmap-opacity": ["interpolate", ["linear"], ["zoom"], 6.5, 0.85, 9, 0],
    },
  });

  // Sonar ping rings — two layers run in antiphase for a continuous pulse; the
  // radius/opacity are animated each frame in the rAF loop (animatePings).
  for (const id of ["vessel-ping-1", "vessel-ping-2"]) {
    map.addLayer({
      id,
      type: "circle",
      source: "vessel-pings",
      layout: { visibility: "none" },
      paint: {
        "circle-radius": 6,
        "circle-color": "rgba(0,0,0,0)",
        // Red at a slaughterhouse, mint at a farm — the "touch" colour-codes.
        "circle-stroke-color": ["match", ["get", "state"], "reception", "#E63926", "#43c6a8"],
        "circle-stroke-width": 2,
        "circle-stroke-opacity": 0.8,
      },
    });
  }

  // Track casing + line, beneath the markers.
  map.addLayer({
    id: "vessel-track-casing",
    type: "line",
    source: "vessel-track",
    layout: { "line-cap": "round", "line-join": "round" },
    paint: {
      "line-color": "#03130d",
      "line-width": ["interpolate", ["linear"], ["zoom"], 6, 3.5, 12, 7],
      "line-opacity": 0.5,
    },
  });
  map.addLayer({
    id: "vessel-track-line",
    type: "line",
    source: "vessel-track",
    layout: { "line-cap": "round", "line-join": "round" },
    paint: {
      "line-color": "#7be3c6",
      "line-width": ["interpolate", ["linear"], ["zoom"], 6, 1.4, 12, 3],
      "line-opacity": 0.95,
    },
  });

  // Live-operation halo: a coloured ring around vessels with a non-null
  // current_op, beneath the marker so the state pictogram stays on top. Only
  // the four known ops (null → no halo).
  map.addLayer({
    id: "vessel-curop",
    type: "symbol",
    source: "vessels",
    minzoom: 7, // overview is the heatmap; pictograms fade in past here
    filter: curopFilter,
    layout: {
      visibility: "none",
      "icon-image": [
        "match",
        ["get", "current_op"],
        "slaughter", "vcurop-slaughter",
        "delousing", "vcurop-delousing",
        "harvest", "vcurop-harvest",
        "smolt", "vcurop-smolt",
        "vcurop-slaughter",
      ],
      "icon-size": [
        "interpolate",
        ["linear"],
        ["zoom"],
        6,
        ["*", 0.42, ROLE_SIZE],
        9,
        ["*", 0.66, ROLE_SIZE],
        13,
        ["*", 1.05, ROLE_SIZE],
      ],
      "icon-allow-overlap": true,
      "icon-ignore-placement": true,
    },
  });

  map.addLayer({
    id: "vessel-markers",
    type: "symbol",
    source: "vessels",
    minzoom: 7,
    layout: {
      visibility: "none", // off by default — vessels are opt-in over the noise map
      // Explicit literal image ids (more robust than concat for icon-image).
      "icon-image": [
        "match",
        ["get", "state"],
        "transit",
        "vstate-transit",
        "reception",
        "vstate-reception",
        "farm",
        "vstate-farm",
        "vstate-idle",
      ],
      // Role (size band) × zoom. Larger than before — clustering keeps the
      // low-zoom view clean, so individual markers can afford to be legible.
      "icon-size": [
        "interpolate",
        ["linear"],
        ["zoom"],
        6,
        ["*", 0.42, ROLE_SIZE],
        9,
        ["*", 0.66, ROLE_SIZE],
        13,
        ["*", 1.05, ROLE_SIZE],
      ],
      "icon-allow-overlap": true,
      // Point every ship the way it's actually heading (course over ground).
      "icon-rotate": ["coalesce", ["get", "cog"], 0],
      "icon-rotation-alignment": "map",
    },
  });

  map.addLayer({
    id: "vessel-laden",
    type: "symbol",
    source: "vessels",
    minzoom: 7,
    filter: ["==", ["get", "laden"], true],
    layout: {
      visibility: "none",
      "icon-image": "vbadge-fish",
      "icon-size": ["interpolate", ["linear"], ["zoom"], 4, 0.26, 9, 0.4, 13, 0.6],
      "icon-offset": [16, -16],
      "icon-allow-overlap": true,
      "icon-ignore-placement": true,
    },
  });

  // op_role badge: a small coloured tag at the top-left corner, distinct from
  // the laden fish badge (top-right). Only vessels with a known op_role.
  map.addLayer({
    id: "vessel-oprole",
    type: "symbol",
    source: "vessels",
    minzoom: 7,
    filter: oproleFilter,
    layout: {
      visibility: "none",
      "icon-image": [
        "match",
        ["get", "op_role"],
        "smolt carrier", "voprole-smolt carrier",
        "harvest wellboat", "voprole-harvest wellboat",
        "delousing / treatment", "voprole-delousing / treatment",
        "service / feed", "voprole-service / feed",
        "voprole-service / feed",
      ],
      "icon-size": ["interpolate", ["linear"], ["zoom"], 4, 0.22, 9, 0.34, 13, 0.5],
      "icon-offset": [-16, -16],
      "icon-allow-overlap": true,
      "icon-ignore-placement": true,
    },
  });
}

// ---------------------------------------------------------------------------
// Filtering / visibility
// ---------------------------------------------------------------------------

const VESSEL_LAYERS = [
  "vessel-heat",
  "vessel-ping-1",
  "vessel-ping-2",
  "vessel-curop",
  "vessel-markers",
  "vessel-laden",
  "vessel-oprole",
];

// An "operation overlay" driven by the Operasjoner nå board: when set, the fleet
// is shown (even if the layer toggle is off) and narrowed to the vessels doing
// the selected operations — matched by live current_op and/or role (service).
let opOverlay: { currentOps: string[]; roles: string[] } | null = null;

// Heatmap + sonar pings are ambience tied to the real layer toggle; only the
// pictogram layers come on for an op overlay.
const VESSEL_AMBIENT = new Set(["vessel-heat", "vessel-ping-1", "vessel-ping-2"]);

function filterExpr(f: VesselFilter): maplibregl.FilterSpecification {
  const parts: maplibregl.ExpressionSpecification[] = [
    ["in", ["get", "role"], ["literal", [...f.roles]]],
  ];
  if (f.ladenOnly) parts.push(["==", ["get", "laden"], true]);
  // Only vessels that have been active at the selected operator's farms.
  if (f.operatorSlug) parts.push(["in", f.operatorSlug, ["get", "operators"]]);
  // Op overlay: keep only vessels whose live op (or role, for service) matches.
  if (opOverlay) {
    const ors: maplibregl.ExpressionSpecification[] = [];
    if (opOverlay.currentOps.length)
      ors.push(["in", ["get", "current_op"], ["literal", opOverlay.currentOps]]);
    if (opOverlay.roles.length)
      ors.push(["in", ["get", "role"], ["literal", opOverlay.roles]]);
    parts.push(ors.length ? (["any", ...ors] as maplibregl.ExpressionSpecification) : ["==", 1, 0]);
  }
  return ["all", ...parts] as maplibregl.FilterSpecification;
}

// Set the per-layer filters from `f` (+ any op overlay). Pulled out of
// applyVesselFilter so the overlay can re-apply without re-emitting the count
// (which would re-enter the analytics board and loop).
function applyLayerFilters(map: maplibregl.Map, f: VesselFilter): void {
  if (map.getLayer("vessel-markers")) map.setFilter("vessel-markers", filterExpr(f));
  if (map.getLayer("vessel-laden"))
    map.setFilter("vessel-laden", [
      "all",
      filterExpr(f),
      ["==", ["get", "laden"], true],
    ] as maplibregl.FilterSpecification);
  if (map.getLayer("vessel-oprole"))
    map.setFilter("vessel-oprole", [
      "all",
      filterExpr(f),
      oproleFilter,
    ] as maplibregl.FilterSpecification);
  if (map.getLayer("vessel-curop"))
    map.setFilter("vessel-curop", [
      "all",
      filterExpr(f),
      curopFilter,
    ] as maplibregl.FilterSpecification);
}

export function applyVesselFilter(map: maplibregl.Map, f: VesselFilter): void {
  lastFilter = f;
  applyLayerFilters(map, f);
  emitCount(f);
}

let vesselsVisible = false;

// Reconcile every vessel layer's visibility from the toggle state + the op
// overlay. Pictograms come on for either; ambience only for the real toggle.
function applyVesselVisibility(map: maplibregl.Map): void {
  const overlayOn = opOverlay != null;
  for (const id of VESSEL_LAYERS) {
    if (!map.getLayer(id)) continue;
    const show = VESSEL_AMBIENT.has(id) ? vesselsVisible : vesselsVisible || overlayOn;
    map.setLayoutProperty(id, "visibility", show ? "visible" : "none");
  }
  if (vesselsVisible) startPings(map);
  else {
    stopPings();
    if (!overlayOn) clearTrack(map);
  }
}

export function setVesselsVisible(map: maplibregl.Map, on: boolean): void {
  vesselsVisible = on;
  applyVesselVisibility(map);
}

// Drive the op overlay from the Operasjoner nå board. `null` (or empty arrays)
// clears it, restoring the fleet to its plain toggle + filter state.
export function setVesselOpOverlay(
  map: maplibregl.Map,
  overlay: { currentOps: string[]; roles: string[] } | null,
): void {
  opOverlay =
    overlay && (overlay.currentOps.length || overlay.roles.length) ? overlay : null;
  if (lastFilter) applyLayerFilters(map, lastFilter);
  applyVesselVisibility(map);
}

// ---------------------------------------------------------------------------
// Sonar pings · a slow pulse out of every location a vessel is touching right
// now (at a farm / at a slaughterhouse). On-brand for Phoniq (sound rings), and
// it makes the coast feel alive without faking vessel movement.
// ---------------------------------------------------------------------------

// Ping only the slaughterhouse arrivals (state "reception") — the meaningful
// "touch". Everyone else is a heading-rotated ship silhouette, no pulse.
function computePings(): VesselFC {
  const feats = positions.features.filter((f) => f.properties.state === "reception");
  return { type: "FeatureCollection", features: feats };
}

function refreshPings(map: maplibregl.Map): void {
  (map.getSource("vessel-pings") as maplibregl.GeoJSONSource | undefined)?.setData(computePings());
}

const PING_PERIOD = 3400; // ms · "slow" pulse
const PING_MIN = 5;
const PING_MAX = 30;
let pingRAF = 0;
let pingStart = 0;

function startPings(map: maplibregl.Map): void {
  if (pingRAF) return;
  const tick = (t: number): void => {
    if (!vesselsVisible) {
      pingRAF = 0;
      return;
    }
    if (!pingStart) pingStart = t;
    const base = ((t - pingStart) % PING_PERIOD) / PING_PERIOD;
    for (const [layer, off] of [["vessel-ping-1", 0], ["vessel-ping-2", 0.5]] as const) {
      if (!map.getLayer(layer)) continue;
      const p = (base + off) % 1; // 0→1 expanding ring, antiphase between the two
      map.setPaintProperty(layer, "circle-radius", PING_MIN + p * (PING_MAX - PING_MIN));
      // Ease-out fade; brightest at birth, gone at full radius.
      map.setPaintProperty(layer, "circle-stroke-opacity", (1 - p) * (1 - p) * 0.85);
      map.setPaintProperty(layer, "circle-stroke-width", 2.4 - p * 1.2);
    }
    pingRAF = requestAnimationFrame(tick);
  };
  pingRAF = requestAnimationFrame(tick);
}

function stopPings(): void {
  if (pingRAF) cancelAnimationFrame(pingRAF);
  pingRAF = 0;
  pingStart = 0;
}

export type VesselSummary = {
  total: number;
  laden: number;
  byRole: Record<string, number>;
  byState: Record<string, number>;
  byCurrentOp: Record<string, number>;
};

// The last filter applied to the layer, so the analytics summary can match the
// markers that are actually drawn (same roles / laden / owner).
let lastFilter: VesselFilter | null = null;

function passesVesselFilter(p: VesselProps, f: VesselFilter | null): boolean {
  if (!f) return true;
  if (!f.roles.has(p.role)) return false;
  if (f.ladenOnly && !p.laden) return false;
  if (f.operatorSlug && !(p.operators ?? []).includes(f.operatorSlug)) return false;
  return true;
}

/**
 * Aggregate the live fleet for the analytics "Skipstrafikk" board. When `bounds`
 * is given, only vessels inside the current map view are counted (and the active
 * layer filter is honoured) so the panel matches what's on screen.
 */
export function getVesselSummary(bounds?: maplibregl.LngLatBounds): VesselSummary {
  const s: VesselSummary = {
    total: 0,
    laden: 0,
    byRole: { wellboat: 0, carrier: 0, service: 0 },
    byState: { transit: 0, reception: 0, farm: 0, idle: 0 },
    byCurrentOp: { slaughter: 0, delousing: 0, harvest: 0, smolt: 0 },
  };
  for (const f of positions.features) {
    const p = f.properties;
    if (bounds) {
      const lon = f.geometry.coordinates[0];
      const lat = f.geometry.coordinates[1];
      if (lon == null || lat == null || !bounds.contains([lon, lat])) continue;
      if (!passesVesselFilter(p, lastFilter)) continue;
    }
    s.total += 1;
    if (p.laden) s.laden += 1;
    if (p.role in s.byRole) s.byRole[p.role] = (s.byRole[p.role] ?? 0) + 1;
    if (p.state in s.byState) s.byState[p.state] = (s.byState[p.state] ?? 0) + 1;
    if (p.current_op && p.current_op in s.byCurrentOp)
      s.byCurrentOp[p.current_op] = (s.byCurrentOp[p.current_op] ?? 0) + 1;
  }
  return s;
}

function emitCount(f: VesselFilter): void {
  if (!onCountChange) return;
  const shown = positions.features.filter((feat) =>
    passesVesselFilter(feat.properties, f),
  ).length;
  onCountChange(shown, positions.features.length);
}

// ---------------------------------------------------------------------------
// Track + detail panel
// ---------------------------------------------------------------------------

function clearTrack(map: maplibregl.Map): void {
  (map.getSource("vessel-track") as maplibregl.GeoJSONSource | undefined)?.setData({
    type: "FeatureCollection",
    features: [],
  });
}

async function showTrack(map: maplibregl.Map, mmsi: number, days = 7): Promise<void> {
  try {
    const res = await fetch(`${API_BASE}/v1/vessels/${mmsi}/track?days=${days}`);
    if (!res.ok) return;
    const feat = (await res.json()) as GeoJSON.Feature;
    const src = map.getSource("vessel-track") as maplibregl.GeoJSONSource | undefined;
    if (!src) return;
    src.setData(
      feat.geometry
        ? { type: "FeatureCollection", features: [feat] }
        : { type: "FeatureCollection", features: [] },
    );
  } catch {
    /* track is a nicety; ignore failures */
  }
}

function ageText(ageH: number | null): string {
  if (ageH == null) return "ukjent";
  if (ageH < 1) return `${Math.round(ageH * 60)} min siden`;
  if (ageH < 48) return `${ageH.toFixed(1)} t siden`;
  return `${Math.round(ageH / 24)} d siden`;
}

const OP_LABEL: Record<string, string> = {
  harvest: "slakting / brønnbåttransport",
  delousing: "avlusing",
};

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] ?? c,
  );
}

// MapLibre serialises array/object feature properties to JSON strings when read
// back from a click/query event. Restore arrays so `.find`/`.includes` work.
function readArrayProp(raw: unknown): string[] {
  if (Array.isArray(raw)) return raw as string[];
  if (typeof raw === "string" && raw.startsWith("[")) {
    try {
      const v = JSON.parse(raw);
      return Array.isArray(v) ? (v as string[]) : [];
    } catch {
      return [];
    }
  }
  return [];
}

// Registers and vessel types arrive UPPERCASE; title-case for display.
function titleCaseCompany(s: string): string {
  return s.toLowerCase().replace(/\b([a-zæøå])/g, (m) => m.toUpperCase());
}

// The aqua company to open in Insight for this vessel: its own owner when the
// owner is a registered operator (slug ends in the owner org-nr), otherwise the
// operator the vessel currently serves. Insight only covers aqua companies, so
// pure shipping owners (e.g. Karmsund) fall through to the served operator.
function vesselInsight(p: VesselProps): { url: string; label: string } | null {
  if (p.owner_org) {
    const ownSlug = p.operators.find((s) => s.endsWith(`-${p.owner_org}`));
    if (ownSlug) return { url: insightCompanyUrl(ownSlug), label: "Åpne eier i Insight" };
  }
  const served = p.operators.find(Boolean);
  if (served) return { url: insightCompanyUrl(served), label: "Åpne operatør i Insight" };
  return null;
}

// One reused popup, anchored to the clicked vessel on the map (consistent with
// the farm popup). Closing it clears the 7-day track.
// A docked card (not a popup anchored on the vessel) so it never covers the
// track we draw to the vessel's origin site. Markers flag each voyage stop.
let voyageMarkers: maplibregl.Marker[] = [];

function detailPanel(): HTMLElement {
  let el = document.getElementById("vessel-detail");
  if (el) return el;
  el = document.createElement("aside");
  el.id = "vessel-detail";
  el.className = "vessel-detail is-hidden";
  document.body.appendChild(el);
  return el;
}

function closeDetail(map: maplibregl.Map): void {
  document.getElementById("vessel-detail")?.classList.add("is-hidden");
  clearTrack(map);
  for (const m of voyageMarkers) m.remove();
  voyageMarkers = [];
}

function openDetail(map: maplibregl.Map, p: VesselProps, lngLat: [number, number]): void {
  const st = STATE_META[p.state];
  const role = VESSEL_ROLES.find((r) => r.key === p.role);
  const rows: string[] = [];
  const row = (k: string, v: string): string =>
    `<div class="vd-row"><span class="vd-k">${k}</span><span class="vd-v">${v}</span></div>`;
  rows.push(row("Type", `${role?.label ?? p.role}${p.length_m ? ` · ${p.length_m} m` : ""}`));
  rows.push(
    row(
      "Status",
      `<span class="vd-dot" style="background:${st.color}"></span>${st.label}`,
    ),
  );
  if (p.current_op && CURRENT_OP_META[p.current_op]) {
    const cur = CURRENT_OP_META[p.current_op];
    rows.push(
      row(
        "Pågående operasjon",
        `<span class="vd-dot" style="background:${cur.color}"></span>${cur.label}`,
      ),
    );
  }
  if (p.op_role && OPERATION_META[p.op_role]) {
    const op = OPERATION_META[p.op_role];
    rows.push(
      row(
        "Driftsrolle",
        `<span class="vd-dot" style="background:${op.color}"></span>${op.label}`,
      ),
    );
  }
  if (p.laden) {
    rows.push(row("Antatt last", "fisk om bord"));
    const origin = [p.from_site, p.from_operator].filter(Boolean).join(" · ");
    if (origin) rows.push(row("Lastet ved", esc(origin)));
  }
  rows.push(row("Fart", `${p.sog.toFixed(1)} kn${p.cog != null ? ` · ${Math.round(p.cog)}°` : ""}`));
  rows.push(row("Sist sett", ageText(p.age_h)));
  if (p.last_op)
    rows.push(
      row(
        "Siste operasjon",
        `${OP_LABEL[p.last_op] ?? p.last_op}${
          p.last_op_at ? ` · ${new Date(p.last_op_at).toLocaleDateString()}` : ""
        }`,
      ),
    );
  const ownerName = p.owner ?? p.operator_company;
  if (ownerName) rows.push(row("Eier / reder", esc(titleCaseCompany(ownerName))));

  // Primary deep-link: this vessel's own page in Insight (/v/{mmsi}). Secondary:
  // the most relevant aqua company (its owner if a registered operator, else the
  // operator it currently serves).
  const vesselLink =
    `<a class="vd-insight" href="${insightVesselUrl(p.mmsi)}" target="_blank" rel="noopener">` +
    `Åpne fartøy i Insight →</a>`;
  const ins = vesselInsight(p);
  const companyLink = ins
    ? `<a class="vd-insight vd-insight-sec" href="${ins.url}" target="_blank" rel="noopener">${ins.label} →</a>`
    : "";

  const el = detailPanel();
  el.innerHTML = `
    <button class="vd-close" aria-label="Lukk">×</button>
    <div class="vd-name">${p.name ? esc(p.name) : "Ukjent fartøy"}</div>
    <div class="vd-sub">MMSI ${p.mmsi}${p.vessel_type ? ` · ${esc(titleCaseCompany(p.vessel_type))}` : ""}</div>
    <div class="vd-rows">${rows.join("")}</div>
    <div class="vd-voyage"><div class="vd-vlabel">Reise</div><div class="muted vd-vbody">laster …</div></div>
    <div class="vd-links">${vesselLink}${companyLink}</div>
    <div class="vd-foot">Sporet viser siste 24 t</div>`;
  el.classList.remove("is-hidden");
  el.querySelector(".vd-close")?.addEventListener("click", () => closeDetail(map));
  void loadVoyage(map, p.mmsi, lngLat, el.querySelector(".vd-vbody") as HTMLElement);
}

type VoyageLeg = {
  locality_no: number; name: string | null;
  operator: string | null; operator_slug: string | null;
  lon: number | null; lat: number | null;
  role: "source" | "staging";
  dwell_min: number | null; hours_since: number | null;
  dist_plant_m: number | null; nearest_plant: string | null;
};
type Voyage = {
  state: string | null;
  chain: VoyageLeg[];
  source: VoyageLeg | null;
  staging: VoyageLeg | null;
  destination: string | null;
  carried: {
    est_t: number | null; vessel_capacity_t: number | null;
    site_biomass_t: number | null; site_capacity_t: number | null;
    site_basis: string | null; anchored_to: string | null;
    confidence: string; note: string;
  } | null;
};

function dwellText(min: number | null): string {
  if (min == null) return "";
  if (min < 90) return `${Math.round(min)} min`;
  return `${(min / 60).toFixed(1)} t`;
}

// Two company names refer to the same firm (for the "slaughterhouse-owned pen"
// tell): compare on a normalised stem.
function sameCompany(a: string | null, b: string | null): boolean {
  if (!a || !b) return false;
  const norm = (s: string) => s.toLowerCase().replace(/\b(as|asa|avd.*|sa)\b/g, "").replace(/[^a-zæøå]/g, "");
  const na = norm(a), nb = norm(b);
  return na.length > 3 && (na.startsWith(nb.slice(0, 6)) || nb.startsWith(na.slice(0, 6)));
}

// Lazy-load the vessel's current voyage and render the harvest STORY —
// source farm → staging pen by the plant → slaughter — into the docked card,
// framing the map on the whole chain with a marker per stop.
async function loadVoyage(
  map: maplibregl.Map, mmsi: number, vessel: [number, number], el: HTMLElement | null,
): Promise<void> {
  if (!el) return;
  try {
    const res = await fetch(`${API_BASE}/v1/vessels/${mmsi}/voyage`);
    if (!res.ok) throw new Error(String(res.status));
    const v = (await res.json()) as Voyage;
    if (!v.chain || v.chain.length === 0) {
      el.textContent = "Ingen nylig anleggsvisitt funnet.";
      return;
    }

    // Frame the whole voyage (all stops + the vessel) in the clear map area and
    // drop a marker per stop.
    for (const m of voyageMarkers) m.remove();
    voyageMarkers = [];
    const bounds = new maplibregl.LngLatBounds(vessel, vessel);
    for (const leg of v.chain) {
      if (leg.lon == null || leg.lat == null) continue;
      bounds.extend([leg.lon, leg.lat]);
      const dot = document.createElement("div");
      dot.className = leg.role === "staging" ? "voyage-dot is-staging" : "voyage-dot is-source";
      voyageMarkers.push(
        new maplibregl.Marker({ element: dot }).setLngLat([leg.lon, leg.lat]).addTo(map),
      );
    }
    const narrow = window.innerWidth < 820;
    const padding = narrow
      ? { top: 80, bottom: 300, left: 30, right: 30 }
      : { top: 90, bottom: 230, left: 330, right: 320 };
    map.fitBounds(bounds, { padding, maxZoom: 12, duration: 700 });

    // Build the story as an ordered stepper: each stop + the slaughter end.
    const steps: string[] = [];
    for (const leg of v.chain) {
      const isSource = leg.role === "source";
      const owned = leg.role === "staging" && sameCompany(leg.operator, v.destination)
        ? ' · <span class="vd-owned">slakteri-eid</span>' : "";
      const kind = isSource ? "Kilde" : "Mellomlager";
      const sub = [
        leg.operator ? titleCaseCompany(leg.operator) : null,
        leg.dwell_min ? `${isSource ? "lastet" : "holdt"} ${dwellText(leg.dwell_min)}` : null,
        leg.role === "staging" && leg.nearest_plant
          ? `ved ${titleCaseCompany(leg.nearest_plant)}` : null,
      ].filter(Boolean).join(" · ");
      steps.push(
        `<div class="vd-step vd-step-${leg.role}">` +
        `<span class="vd-stepdot"></span>` +
        `<div><div class="vd-stepname">${kind}: <strong>${esc(leg.name ?? "ukjent")}</strong>${owned}</div>` +
        (sub ? `<div class="vd-vsub">${esc(sub)}</div>` : "") + `</div></div>`,
      );
    }
    if (v.destination) {
      steps.push(
        `<div class="vd-step vd-step-slaughter"><span class="vd-stepdot"></span>` +
        `<div class="vd-stepname">Slakt: <strong>${esc(titleCaseCompany(v.destination))}</strong></div></div>`,
      );
    }

    // Carried fish — anchored to the SOURCE farm, clearly an upper estimate.
    let cargo = "";
    if (v.carried && v.carried.est_t != null) {
      const c = v.carried;
      const cap = c.vessel_capacity_t != null ? `fartøy ~${c.vessel_capacity_t} t` : "";
      const siteT = c.site_biomass_t ?? c.site_capacity_t;
      const siteLbl = c.site_basis === "capacity" ? "kapasitet" : "biomasse";
      const siteStr = siteT != null ? `kilde ${siteT} t (${siteLbl})` : "";
      const basis = [cap, siteStr].filter(Boolean).join(" · ");
      cargo =
        `<div class="vd-vcargo" title="${esc(c.note)}">` +
        `≈ <strong>${c.est_t} t</strong> fisk · øvre anslag, lav sikkerhet` +
        (basis ? `<div class="vd-vsub">${basis}</div>` : "") + `</div>`;
    }

    const src = v.source ?? v.chain[0];
    const siteLink = src
      ? `<a class="vd-insight vd-insight-sec" href="${insightLocationUrl(src.locality_no)}" ` +
        `target="_blank" rel="noopener">Åpne kildeanlegg i Insight →</a>`
      : "";

    el.classList.remove("muted");
    el.innerHTML = `<div class="vd-steps">${steps.join("")}</div>${cargo}${siteLink}`;
  } catch {
    el.textContent = "Reisedata utilgjengelig.";
  }
}

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------

export async function initVessels(map: maplibregl.Map): Promise<void> {
  registerIcons(map);
  addLayers(map);

  const hover = new maplibregl.Popup({
    closeButton: false,
    closeOnClick: false,
    offset: 14,
    className: "vessel-tip",
  });
  map.on("mouseenter", "vessel-markers", (e) => {
    map.getCanvas().style.cursor = "pointer";
    const f = e.features?.[0];
    if (!f) return;
    const p = f.properties as unknown as VesselProps;
    const g = f.geometry as GeoJSON.Point;
    hover
      .setLngLat(g.coordinates as [number, number])
      .setHTML(`<strong>${p.name ? esc(p.name) : "Ukjent"}</strong><br>${STATE_META[p.state].label}`)
      .addTo(map);
  });
  map.on("mouseleave", "vessel-markers", () => {
    map.getCanvas().style.cursor = "";
    hover.remove();
  });
  map.on("click", "vessel-markers", (e) => {
    const f = e.features?.[0];
    if (!f) return;
    const raw = f.properties as Record<string, unknown>;
    // operators comes back as a JSON string from the map event; restore it.
    const p = {
      ...(raw as unknown as VesselProps),
      operators: readArrayProp(raw.operators),
    };
    const g = f.geometry as GeoJSON.Point;
    openDetail(map, p, g.coordinates as [number, number]);
    void showTrack(map, p.mmsi, 1); // last day — shows which site it came from
  });

  try {
    const res = await fetch(`${API_BASE}/v1/vessels/positions`);
    if (res.ok) {
      positions = (await res.json()) as VesselFC;
      (map.getSource("vessels") as maplibregl.GeoJSONSource | undefined)?.setData(positions);
      refreshPings(map);
      emitCount(defaultVesselFilter());
    }
  } catch {
    /* leave empty */
  }
}
